import React, { useEffect, useMemo, useState } from "react";
import {
  activateWorkspaceBilling,
  closeWorkspaceBilling,
} from "../../services/server";

/**
 * Shared components for the workspace billing lifecycle UI:
 *   - WorkspaceStatusBadge    — pill that reflects deal_status
 *   - ActivateWorkspaceButton — silent activate (no Checkout); shows
 *                                "lägg till kort"-prompt when no PM
 *   - WorkspaceCloseBanner    — "Stängs om X dagar" countdown
 *   - WorkspaceLockOverlay    — read-only veil for pending/closing/closed
 *
 * All four take a workspace object with at minimum `deal_status`,
 * `slug`, and (where relevant) `scheduled_close_at`.
 */

// ── Helpers ──────────────────────────────────────────────────────────

const STATUS_META = {
  pending:  { label: "Väntande",  bg: "#FEF3C7", fg: "#92400E", border: "#FBBF24" },
  active:   { label: "Aktiv",     bg: "#D1FAE5", fg: "#065F46", border: "#10B981" },
  archived: { label: "Arkiv",     bg: "#E0E7FF", fg: "#3730A3", border: "#818CF8" },
  closing:  { label: "Stängs",    bg: "#FEE2E2", fg: "#991B1B", border: "#F87171" },
  closed:   { label: "Stängd",    bg: "#E5E7EB", fg: "#374151", border: "#9CA3AF" },
  inactive: { label: "Inaktiv",   bg: "#E5E7EB", fg: "#374151", border: "#9CA3AF" },
};

function _fmtDateSv(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

function _daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const ms = target - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

// ── Status badge ─────────────────────────────────────────────────────

export function WorkspaceStatusBadge({ status, style }) {
  const meta = STATUS_META[status] || STATUS_META.inactive;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 10px",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        background: meta.bg,
        color: meta.fg,
        border: `1px solid ${meta.border}`,
        borderRadius: 999,
        ...style,
      }}
    >
      {meta.label}
    </span>
  );
}

// ── Close countdown banner ───────────────────────────────────────────

export function WorkspaceCloseBanner({ workspace }) {
  if (!workspace || workspace.deal_status !== "closing") return null;
  const days = _daysUntil(workspace.scheduled_close_at);
  if (days == null) return null;

  return (
    <div
      style={{
        padding: "12px 18px",
        background: "#FEF2F2",
        borderBottom: "1px solid #FCA5A5",
        color: "#7F1D1D",
        fontSize: 14,
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span aria-hidden="true">⏳</span>
      <span>
        Affären stängs om <strong>{days === 0 ? "idag" : `${days} ${days === 1 ? "dag" : "dagar"}`}</strong>
        {workspace.scheduled_close_at && (
          <> (<time>{_fmtDateSv(workspace.scheduled_close_at)}</time>)</>
        )}
        . Workspace är i läsläge — köpare och säljare sparkas ut när nedstängningen slutförs.
      </span>
    </div>
  );
}

// ── Lock overlay (pending / closing / closed) ────────────────────────

const LOCKED_STATES = new Set(["pending", "closing", "closed"]);

// Pages som ska vara läsbara redan i pending-läget (innan workspace har
// aktiverats). Värderingen är hooken som får brokern att aktivera —
// låser man den bakom betalmuren ser brokern aldrig värdet och har
// ingen anledning att klicka aktivera.
export function WorkspaceLockOverlay({ workspace, children, onActivate, allowPending = false }) {
  if (!workspace || !LOCKED_STATES.has(workspace.deal_status)) {
    return <>{children}</>;
  }
  if (allowPending && workspace.deal_status === "pending") {
    return <>{children}</>;
  }

  const meta = STATUS_META[workspace.deal_status] || STATUS_META.inactive;
  const isPending = workspace.deal_status === "pending";
  const isClosed = workspace.deal_status === "closed";

  return (
    <div style={{ position: "relative" }}>
      <div style={{ pointerEvents: "none", opacity: 0.45, filter: "grayscale(0.4)" }}>
        {children}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.55)",
          backdropFilter: "blur(2px)",
          zIndex: 5,
        }}
      >
        <div
          style={{
            background: "#fff",
            border: `1px solid ${meta.border}`,
            borderRadius: 12,
            padding: "20px 28px",
            maxWidth: 460,
            textAlign: "center",
            boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
          }}
        >
          <WorkspaceStatusBadge status={workspace.deal_status} style={{ marginBottom: 10 }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>
            {isPending && "Workspace är väntande"}
            {workspace.deal_status === "closing" && "Workspace håller på att stängas"}
            {isClosed && "Workspace är stängd"}
          </h3>
          <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5, margin: "0 0 14px" }}>
            {isPending && "Aktivera workspace för att låsa upp IM, datarum, due diligence, pipeline och köpar­matchning."}
            {workspace.deal_status === "closing" && (
              <>Stängningen slutförs {workspace.scheduled_close_at ? _fmtDateSv(workspace.scheduled_close_at) : "snart"}. Inga ändringar kan göras tills dess.</>
            )}
            {isClosed && "Affären är avslutad. Köpare och säljare har sparkats ut. Workspacet är bevarat för revision."}
          </p>
          {isPending && typeof onActivate === "function" && workspace.can_manage_lifecycle && (
            <ActivateWorkspaceButton workspace={workspace} onActivated={onActivate} />
          )}
          {isPending && !workspace.can_manage_lifecycle && (
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Endast mäklaren kan aktivera detta workspace.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Activate workspace (silent, no checkout) ────────────────────────

export function ActivateWorkspaceButton({ workspace, onActivated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Re-attempt activate after the broker completes the card popup.
  useEffect(() => {
    function onSetupSucceeded() {
      // Just returned from Stripe Checkout — the PaymentMethod is
      // attached at Stripe's end, but our DB cache lags behind the
      // setup_intent.succeeded webhook by a few hundred ms. Use the
      // syncing-after-stripe mode: silent retries with backoff, no
      // popup re-open on no_payment_method.
      handleActivate({ syncingAfterStripe: true });
    }
    window.addEventListener("billing:setup-succeeded", onSetupSucceeded);
    return () => window.removeEventListener("billing:setup-succeeded", onSetupSucceeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCardPopup(reason = "missing") {
    // Clear the per-session dismissal so the modal actually opens
    // again — otherwise dispatching the event is a no-op because
    // BillingSetupGate's Stripe-config check is skipped.
    try { sessionStorage.removeItem("billing:setup-dismissed"); } catch (_e) { /* noop */ }
    window.dispatchEvent(new CustomEvent("billing:open-setup", { detail: { reason } }));
  }

  async function handleActivate({ syncingAfterStripe = false, attempt = 0 } = {}) {
    if (!workspace?.slug) return;
    setError(null);
    setLoading(true);
    try {
      const resp = await activateWorkspaceBilling(workspace.slug);
      if (typeof onActivated === "function") onActivated(resp);
    } catch (err) {
      // server.js attaches the parsed JSON body as `err.body` (NOT
      // `err.data`); legacy code paths may set `err.data` so we accept
      // either, then fall back to `err` itself for shape tolerance.
      const detail = err?.body || err?.data || err;
      const status = err?.status;
      const code = detail?.code;
      // err.message is "HTTP 400 Bad Request" — never user-facing.
      const userMessage = (m) => (m && !/^HTTP \d/.test(m) ? m : null);

      // Just returned from Stripe + got no_payment_method → sync race
      // (PM attached at Stripe but webhook hasn't landed yet, and our
      // self-heal lookup occasionally returns empty on the first try).
      // Retry silently with backoff; only fall through to the popup
      // path after a handful of attempts.
      if (syncingAfterStripe && code === "no_payment_method" && attempt < 4) {
        const waitMs = 1000 + attempt * 750; // 1.0, 1.75, 2.5, 3.25
        setError("Synkroniserar kort med Stripe…");
        // Keep `loading` true so the button stays disabled across the
        // retry chain — handleActivate's setLoading(true) at the top
        // will re-assert it on the next attempt anyway.
        await new Promise((r) => setTimeout(r, waitMs));
        return handleActivate({ syncingAfterStripe: true, attempt: attempt + 1 });
      }

      // Non-admin clicked Activate → backend can't act on their
      // behalf. Show the "ask your admin"-message instead of trying
      // to open the card popup (popup also requires admin and would
      // 403 on getBillingStatus).
      if (code === "not_billing_admin") {
        setError(
          detail?.detail
          || "Aktivera workspace inte tillgängligt, be din admin att aktivera funktionen genom att klicka aktivera workspace."
        );
        return;
      }

      // Admin without a working card → reopen the card popup so they
      // can add/replace one. Covers no_payment_method, card_expired,
      // declined-card (402), and unexpected 500s. We pass `reason` so
      // the modal heading switches between "Lägg till betalkort" and
      // "Kortet har gått ut".
      if (code === "card_expired") {
        openCardPopup("expired");
      } else if (
        code === "no_payment_method"
        || code === "subscription_incomplete"
        || status === 402
        || status === 500
      ) {
        openCardPopup("missing");
      } else {
        setError(detail?.detail || userMessage(err?.message) || "Aktivering misslyckades.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
      <button
        type="button"
        onClick={handleActivate}
        disabled={loading}
        style={primaryBtn(loading)}
      >
        {loading ? "Aktiverar…" : "Aktivera workspace"}
      </button>
      {error && (
        <div style={{ fontSize: 12, color: "#991B1B" }}>{error}</div>
      )}
    </div>
  );
}

// ── Close workspace button (broker-initiated) ───────────────────────

export function CloseWorkspaceButton({ workspace, onClosed, label = "Stäng affär", hideBilling = false, buttonStyle = null }) {
  const [loading, setLoading] = useState(false);
  // `loadingMode` = vilken variant som rullar — gör att vi kan spinna
  // bara rätt knapp ("Stäng nu" eller "Stäng om 7 dagar") medan den
  // andra hålls disabled men inte spinnar.
  const [loadingMode, setLoadingMode] = useState(null);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canClose = useMemo(
    () =>
      (workspace?.deal_status === "active" || workspace?.deal_status === "archived")
      && workspace?.can_manage_lifecycle,
    [workspace?.deal_status, workspace?.can_manage_lifecycle],
  );

  if (!canClose) return null;

  async function handleClose(immediate) {
    setError(null);
    setLoading(true);
    setLoadingMode(immediate ? "now" : "scheduled");
    try {
      const resp = await closeWorkspaceBilling(workspace.slug, { immediate });
      if (typeof onClosed === "function") onClosed(resp);
      setDialogOpen(false);
    } catch (err) {
      const detail = err?.body || err?.data || err;
      const friendly = err?.message && !/^HTTP \d/.test(err.message) ? err.message : null;
      setError(detail?.detail || friendly || "Stängning misslyckades.");
    } finally {
      setLoading(false);
      setLoadingMode(null);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => { setError(null); setDialogOpen(true); }}
        style={buttonStyle || dangerBtn(false)}
      >
        {label}
      </button>
      {dialogOpen && (
        <CloseWorkspaceDialog
          loading={loading}
          loadingMode={loadingMode}
          error={error}
          hideBilling={hideBilling}
          onClose={() => { if (!loading) setDialogOpen(false); }}
          onConfirm={handleClose}
        />
      )}
    </>
  );
}

function CloseWorkspaceDialog({ loading, loadingMode, error, onClose, onConfirm, hideBilling = false }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "min(520px, 100%)",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 28,
          borderRadius: 12,
          boxShadow: "0 24px 56px rgba(0,0,0,0.25)",
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 6px" }}>
          {hideBilling ? "Avsluta projekt" : "Stäng affär"}
        </h2>
        <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5, margin: "0 0 18px" }}>
          {hideBilling
            ? "Välj när stängningen ska gälla. Köpare och säljare sparkas ut."
            : "Välj när stängningen ska gälla. Stripe-prenumerationen sägs upp samtidigt."}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Stäng nu — finalize direkt, eject motparter, cancel Stripe */}
          <button
            type="button"
            onClick={() => onConfirm(true)}
            disabled={loading}
            style={{
              ...dangerBtn(loading && loadingMode !== "now"),
              padding: "14px 18px",
              textAlign: "left",
              opacity: loading && loadingMode !== "now" ? 0.55 : 1,
            }}
          >
            <div style={{ fontWeight: 700 }}>
              {loading && loadingMode === "now" ? "Stänger…" : "Stäng nu"}
            </div>
            <div style={{ fontSize: 12, fontWeight: 400, marginTop: 4, opacity: 0.9 }}>
              Låser workspacet direkt.
            </div>
          </button>

          {/* Stäng om 7 dagar — initiate close, countdown, Stripe cancel_at */}
          <button
            type="button"
            onClick={() => onConfirm(false)}
            disabled={loading}
            style={{
              ...dangerBtn(loading && loadingMode !== "scheduled"),
              padding: "14px 18px",
              textAlign: "left",
              background: "#fff",
              color: "#991B1B",
              border: "1px solid #FCA5A5",
              opacity: loading && loadingMode !== "scheduled" ? 0.55 : 1,
            }}
          >
            <div style={{ fontWeight: 700 }}>
              {loading && loadingMode === "scheduled" ? "Stänger…" : "Stäng om 7 dagar"}
            </div>
            <div style={{ fontSize: 12, fontWeight: 400, marginTop: 4 }}>
              Läsläge nu, finaliseras om 7 dagar (eller månadens slut).
            </div>
          </button>
        </div>

        {error && (
          <div style={{
            marginTop: 14,
            padding: "10px 12px",
            background: "#FEF2F2",
            border: "1px solid #FCA5A5",
            borderRadius: 8,
            fontSize: 13,
            color: "#991B1B",
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={secondaryBtn(loading)}
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shared button styles ────────────────────────────────────────────

function primaryBtn(disabled) {
  return {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 600,
    background: disabled ? "#9ca3af" : "#1C1917",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: disabled ? "wait" : "pointer",
  };
}

function dangerBtn(disabled) {
  return {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 600,
    background: disabled ? "#9ca3af" : "#991B1B",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: disabled ? "wait" : "pointer",
  };
}

function secondaryBtn(disabled) {
  return {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 500,
    background: "#fff",
    color: disabled ? "#9ca3af" : "#374151",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}
